import { ArrowRight, Bus, MoveRight } from "lucide-react";
import { useTranslation } from "../../i18n/LanguageContext";

export default function TransferService({ onQuoteClick }) {
  const { t } = useTranslation();
  const tr = t.transfer;

  return (
    <section
      id="transzfer"
      data-testid="transfer-section"
      className="relative py-16 md:py-32 bg-[#dfd3ee] overflow-hidden"
    >
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-16 md:h-24 bg-gradient-to-b from-transparent to-[#ebe2f5]" />
      <div className="pointer-events-none absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#83588f] opacity-[0.09] blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#83588f] opacity-[0.08] blur-[120px]" />

      <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-12 relative">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-12 lg:gap-20 items-end mb-14 md:mb-20">
          <div>
            <p className="text-eyebrow">{tr.eyebrow}</p>
            <h2
              data-testid="transfer-title"
              className="mt-5 font-serif-display text-[34px] leading-[1.1] sm:text-4xl md:text-5xl lg:text-6xl text-[#0a0a0a] sm:leading-[1.04]"
            >
              {tr.titleA}{" "}
              <span className="italic gold-text">{tr.titleB}</span>
            </h2>
          </div>
          <p className="text-[#8a7f8f] leading-relaxed max-w-xl">{tr.intro}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {tr.routes.map((route, i) => (
            <article
              key={i}
              data-testid={`transfer-route-${i}`}
              className="group relative rounded-3xl bg-[#f5effb] border border-white/5 hover:border-[#0a0a0a]/30 transition-all duration-500 p-6 sm:p-7 md:p-8 flex flex-col"
            >
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#0a0a0a]">
                {tr.routeLabel} · {String(i + 1).padStart(2, "0")}
              </div>

              <div className="mt-6 flex items-center gap-3">
                <h3 className="font-serif-display text-2xl md:text-[28px] text-[#0a0a0a] leading-tight">
                  {route.from}
                </h3>
                <MoveRight
                  size={18}
                  className="text-[#0a0a0a] shrink-0"
                  strokeWidth={1.5}
                />
                <span className="font-serif-display text-2xl md:text-[28px] text-[#1a1a1a] italic">
                  {tr.arrival}
                </span>
              </div>

              <div className="gold-line mt-7" />

              <dl className="mt-6 space-y-3 text-sm">
                <div className="flex items-start justify-between gap-4">
                  <dt className="text-[#8a7f8f] text-xs uppercase tracking-[0.18em]">
                    {tr.carLabel}
                  </dt>
                  <dd className="text-[#594f5a] text-right">{route.detail}</dd>
                </div>
                <div className="flex items-start justify-between gap-4">
                  <dt className="text-[#8a7f8f] text-xs uppercase tracking-[0.18em]">
                    {tr.durationLabel}
                  </dt>
                  <dd className="text-[#1a1a1a] text-right">
                    {route.duration}
                  </dd>
                </div>
              </dl>

              <p className="mt-6 text-sm text-[#8a7f8f] leading-relaxed">
                {route.note}
              </p>

              <button
                onClick={onQuoteClick}
                data-testid={`transfer-cta-${i}`}
                className="mt-auto pt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#1a1a1a] hover:text-[#0a0a0a] transition-colors self-start"
              >
                {tr.cta}
                <ArrowRight size={13} />
              </button>
            </article>
          ))}
        </div>

        <div className="mt-12 md:mt-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 p-6 md:p-7 glass-strong rounded-2xl">
          <div className="flex items-center gap-4">
            <span className="w-11 h-11 rounded-full border border-[#0a0a0a]/30 flex items-center justify-center">
              <Bus size={16} className="text-[#0a0a0a]" strokeWidth={1.5} />
            </span>
            <p className="text-[#594f5a] text-sm md:text-base leading-snug">
              {tr.footer}
            </p>
          </div>
          <button
            onClick={onQuoteClick}
            data-testid="transfer-quote-btn"
            className="btn-gold px-6 py-3 rounded-full text-sm inline-flex items-center gap-2 whitespace-nowrap"
          >
            {tr.footerCta}
            <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}
